import { IconButton, Divider, Stack, Typography, Box } from '@mui/material';
import { VyIcon } from '@weft/icons';
import { INVOICE_CALCULATION_STATUSES, INVOICE_REVIEW_STATUSES } from '@weft/types';
import { dayjs, isNotNullOrUndefined, withDefault } from '@weft/utils';
import { useLightInvoices } from 'apps/app/src/hooks/requests/use-get-light-invoices';
import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { EmptyTableOverlay } from '../../../components/design/EmptyTableOverlay';
import { Price } from '../../../components/design/Price';
import { Spinner } from '../../../components/design/Spinner';
import { PdfViewer } from '../../../components/new-design/layout/PdfViewer/PdfViewer';
import { VyPageLayout } from '../../../components/new-design/layout/VyPageLayout';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { useInvoicePdf } from '../../../hooks/requests/use-invoice-pdf';
import { useDownloadInvoicePdf } from '../../../hooks/use-download-invoice-pdf';
import { usePolling } from '../../../hooks/use-polling';
import { useValidatedParams } from '../../../hooks/use-validated-params';
import { InvoiceBillingStatusLabel } from '../InvoicesPage/invoices-page-components/InvoiceBillingStatusLabel';
import { LightInvoiceViewPageActionsMenu } from './light-invoice-view-page-components/LightInvoiceViewPageActionsMenu';
import { LightInvoiceViewPageActivity } from './light-invoice-view-page-components/LightInvoiceViewPageActivity';
import { LightInvoiceViewPageShareMenu } from './light-invoice-view-page-components/LightInvoiceViewPageShareMenu';

type LightInvoiceViewPageProps = {
  doNotUsePageLayout?: boolean;
};

const LightInvoiceViewPage: React.FC<LightInvoiceViewPageProps> = ({ doNotUsePageLayout }) => {
  const { invoiceId } = useValidatedParams<['invoiceId']>('invoiceId');
  const { data: invoicesData, refetch } = useLightInvoices([invoiceId], { suspense: true });
  const [invoice] = withDefault(invoicesData?.invoices, []);

  const { data: invoicePdf } = useInvoicePdf(invoiceId, { suspense: true, enabled: invoice.reviewStatus === INVOICE_REVIEW_STATUSES.Approved });

  usePolling(invoice.calculationStatus === INVOICE_CALCULATION_STATUSES.Calculating, refetch, 2500);

  const handleDownloadPdf = useDownloadInvoicePdf(invoicePdf?.encodedPdf, invoice.id, invoice.customer.name, invoice.billingCycle);

  if (invoice.reviewStatus !== INVOICE_REVIEW_STATUSES.Approved) {
    return (
      <div className="flex w-full h-full items-center justify-center">
        <EmptyTableOverlay>
          This view is available for approved invoices
        </EmptyTableOverlay>
      </div>
    );
  }

  if (invoice.calculationStatus === INVOICE_CALCULATION_STATUSES.Calculating) {
    return <Spinner />;
  }

  const dueDate = dayjs(invoice.dueDate).utc();

  const Content = (
    <>
      <div className="min-w-[300px] max-w-[300px] bg-white h-full overflow-hidden">
        <Stack>
          <Box sx={{
            padding: '24px 24px 32px 24px',
            rowGap: 2,
            display: 'flex',
            flexDirection: 'column',
          }}
          >
            <Typography variant="h6">Invoice Summary</Typography>
          </Box>

          <Divider/>

          <Box sx={{
            padding: '28px 24px',
            rowGap: 2,
            display: 'flex',
            flexDirection: 'column',
          }}
          >
            <Typography variant="body1">{`Amount Due ${dueDate.format('DD.MM.YY')}`}</Typography>
            <Box style={{
              display: 'flex',
              columnGap: '16px',
              alignItems: 'center',
            }}
            >
              <Price variant="h5">{invoice.revenueBreakdown.total}</Price>
              <InvoiceBillingStatusLabel
                billingStatus={invoice.billingStatus}
                sx={{
                  marginBottom: '6px',
                }}
              />
            </Box>
          </Box>

          <Divider/>

          <LightInvoiceViewPageActivity systemEvents={withDefault(invoicesData?.invoicesSystemEvents, [])}/>
        </Stack>
      </div>

      <Stack
        spacing={4}
        sx={{
          width: '100%',
          padding: '32px',
          overflowY: 'auto',
          alignItems: 'center',
        }}
      >
        {isNotNullOrUndefined(invoicePdf) && (
          <PdfViewer
            base64EncodedPdf={invoicePdf.encodedPdf}
          />
        )}
      </Stack>
    </>
  );

  return (
    <>
      <Helmet>
        <title>{`Invoice ${invoice.name}`}</title>
      </Helmet>

      {doNotUsePageLayout
        ? Content
        : (
          <VyPageLayout
            path={[
              <Link to="/invoices">Invoices</Link>,
              <Link to={`/customers/${invoice.customer.id}`}>{invoice.customer.name}</Link>,
              invoice.name,
            ]}
            title={`${invoice.customer.name} - Invoice ${invoice.id.slice(-5)}`}
            sx={{
              padding: 0,
              display: 'flex',
              flexDirection: 'row',
              overflow: 'hidden',
            }}
            action={(
              <div className="flex gap-x-2 items-end">
                <IconButton onClick={handleDownloadPdf}>
                  <VyIcon
                    icon="Download"
                    color="#323232"
                  />
                </IconButton>

                <LightInvoiceViewPageShareMenu invoiceData={invoice}/>

                <Divider
                  orientation="vertical"
                  sx={{ height: '24px', marginX: '8px', marginBottom: '8px' }}
                />

                <LightInvoiceViewPageActionsMenu
                  invoiceId={invoiceId}
                  invoice={invoice}
                />
              </div>
            )}
          >
            {Content}
          </VyPageLayout>
        )}
    </>
  );
};

const LightInvoiceViewPageWithSuspenseBoundary = withSuspenseBoundary(LightInvoiceViewPage);

export { LightInvoiceViewPageWithSuspenseBoundary as LightInvoiceViewPage };
